import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 2000;
const RECONNECT_INTERVAL = 100; // Reduced from 1000ms to 100ms for faster reconnection

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(app, pubsubToken, websocketHost = '') {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);
    this.app = app;
    this.pubsubToken = pubsubToken;
    this.websocketHost = websocketHost;
    this.events = {};
    this.reconnectTimer = null;
    this.isAValidEvent = () => true;
    
    // Create initial subscription
    this.createSubscription();
    
    this.  triggerPresenceInterval = () => {
      setTimeout(() => {
        if (this.subscription && this.subscription.updatePresence) {
          try {
            this.subscription.updatePresence();
          } catch (error) {
            console.error('Error updating presence, attempting reconnection:', error);
            this.forceReconnect();
          }
        }
        this.triggerPresenceInterval();
      }, PRESENCE_INTERVAL);
    };
    this.triggerPresenceInterval();
  }

  createSubscription() {
    // Remove old subscription if it exists
    if (this.subscription) {
      this.consumer.subscriptions.remove(this.subscription);
    }
    
    // Create new subscription
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: this.pubsubToken,
        account_id: this.app.$store.getters.getCurrentAccountId,
        user_id: this.app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        disconnected: () => {
          console.log('ActionCable subscription disconnected, reconnecting immediately...');
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          // Start reconnection immediately
          setTimeout(() => this.checkConnection(), 10);
          this.initReconnectTimer();
        },
        connected: () => {
          console.log('ActionCable subscription connected');
          BaseActionCableConnector.isDisconnected = false;
        },
        rejected: () => {
          console.error('ActionCable subscription rejected, retrying...');
          setTimeout(() => this.createSubscription(), RECONNECT_INTERVAL);
        }
      }
    );
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    
    if (!isConnectionActive) {
      // Connection is down, try to reopen
      this.consumer.connection.reopen();
      this.initReconnectTimer();
    } else if (BaseActionCableConnector.isDisconnected) {
      // Connection is back up but we were disconnected
      // Re-create subscription immediately
      this.createSubscription();
      this.clearReconnectTimer();
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      // Everything is fine, clear the timer
      this.clearReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
  
  // Method to manually trigger reconnection
  forceReconnect() {
    console.log('Forcing ActionCable reconnection...');
    BaseActionCableConnector.isDisconnected = true;
    this.consumer.disconnect();
    setTimeout(() => {
      this.consumer.connection.reopen();
      this.checkConnection();
    }, 100);
  }
}

export default BaseActionCableConnector;
